import React, { useState, useMemo, useCallback, useEffect, useContext } from 'react';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';
import EllipsisPagination from 'components/common/EllipsisPagination/EllipsisPagination';
import { useFirmUserList } from 'hooks/useFirmUserList';
import { generateFullName } from 'utils/generateFullNameUtils';
import './BatchBillingV2.css';
import BatchCreateInvoicesDialog from './components/BatchCreateInvoicesDialog/BatchCreateInvoicesDialog';
import { usePracticeAreasQuery } from 'hooks/queries/usePracticeAreasQuery';
import { useBatchBillingListQuery } from './hooks/queries/useBatchBillingListQuery';
import { UserDetailsContext } from 'context/userDetailsContext';
import { getPageLimit, getSavedPageNumber, getFilePathForPagination } from './helpers/batchBillingHelpers';
import { handlePageNumberInPagination } from 'common/pagination';
import debounce from 'lodash.debounce';

const BatchBillingV2 = () => {
  const userContext = useContext(UserDetailsContext);

  // Include in Selection checkboxes (UI state - not applied until "Apply Filters" is clicked)
  const [includeBillableTime, setIncludeBillableTime] = useState(true);
  const [includeNonBillableTime, setIncludeNonBillableTime] = useState(true);
  const [includeExpenses, setIncludeExpenses] = useState(true);
  const [includeUnpaidInvoice, setIncludeUnpaidInvoice] = useState(true);

  // Dropdowns
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [selectedAttorney, setSelectedAttorney] = useState(null);
  const [selectedParalegal, setSelectedParalegal] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [createBatchVisible, setCreateBatchVisible] = useState(false);

  // Search and pagination
  const [searchKey, setSearchKey] = useState('');
  const [appliedSearchKey, setAppliedSearchKey] = useState('');
  const [currentPage, setCurrentPage] = useState(getSavedPageNumber(userContext) || 1);
  const [pageLimit, setPageLimit] = useState(getPageLimit(userContext) || 10);

  // Applied filters (used for API call) - initialized to match UI checkbox states
  const [appliedFilters, setAppliedFilters] = useState({
    practice_area_list: [],
    lead_attorney_list: [],
    lead_paralegal_list: [],
    uninvoiced_billable_time: true, // Match UI checkbox initial state (includeBillableTime)
    uninvoiced_nonbillable_time: true, // Match UI checkbox initial state (includeNonBillableTime)
    uninvoiced_expenses: true, // Match UI checkbox initial state (includeExpenses)
    unpaid_invoice_balance: true, // Match UI checkbox initial state (includeUnpaidInvoice)
  });

  const isProcessDisabled = selectedRows?.length === 0;

  // Practice Areas (via React Query hook with caching)
  const { practiceAreaList } = usePracticeAreasQuery({
    loadFullList: true,
    status: 'active',
    excludeFromLoading: false,
    sort_order: 'alphabet_ascending',
    pagination_token: '',
    pagination_direction: 'forward',
  });

  const { userList, isFirmUserLoading } = useFirmUserList(false, false, true);

  // Debounced search handler
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setAppliedSearchKey(value);
        setCurrentPage(1); // Reset to first page on search
        // Save page reset to user preferences
        const path = getFilePathForPagination();
        handlePageNumberInPagination(userContext, path.module, path.subModule, 'batch_billing_page', 1);
      }, 500),
    [userContext]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Fetch batch billing data
  const {
    batchBillingList,
    totalRecords,
    currentPage: apiCurrentPage,
    totalPages,
    pageSize,
    isLoading: isBatchBillingLoading,
    isError: isBatchBillingError,
    error: batchBillingError,
    refetch: refetchBatchBilling,
  } = useBatchBillingListQuery({
    limit: pageLimit,
    page: currentPage,
    lead_attorney_list: appliedFilters.lead_attorney_list,
    lead_paralegal_list: appliedFilters.lead_paralegal_list,
    practice_area_list: appliedFilters.practice_area_list,
    search_keyword: appliedSearchKey,
    uninvoiced_billable_time: appliedFilters.uninvoiced_billable_time,
    uninvoiced_nonbillable_time: appliedFilters.uninvoiced_nonbillable_time,
    uninvoiced_expenses: appliedFilters.uninvoiced_expenses,
    unpaid_invoice_balance: appliedFilters.unpaid_invoice_balance,
  });

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchKey(value);
    debouncedSearch(value);
  };

  // Select All handler
  const handleSelectAll = () => {
    if (batchBillingList && batchBillingList.length > 0) {
      setSelectedRows([...batchBillingList]);
    }
  };

  // Deselect All handler
  const handleDeselectAll = () => {
    setSelectedRows([]);
  };

  // Initialize page limit and page number from user preferences
  useEffect(() => {
    if (userContext) {
      const savedPageLimit = getPageLimit(userContext);
      const savedPageNumber = getSavedPageNumber(userContext);
      if (savedPageLimit) {
        setPageLimit(savedPageLimit);
      }
      if (savedPageNumber) {
        setCurrentPage(savedPageNumber);
      }
    }
  }, [userContext]);

  // Pagination handlers
  const onPageChange = (page) => {
    setCurrentPage(page);
    // Save page number to user preferences
    const path = getFilePathForPagination();
    handlePageNumberInPagination(userContext, path.module, path.subModule, 'batch_billing_page', page);
  };

  const onPageSizeChange = (newPageSize) => {
    setPageLimit(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
    // Update pagination preference in user context
    const path = getFilePathForPagination();
    handlePageNumberInPagination(userContext, path.module, path.subModule, path.listName, newPageSize);
    // Also reset saved page number
    handlePageNumberInPagination(userContext, path.module, path.subModule, 'batch_billing_page', 1);
  };

  // Status template to show Active when is_open is true
  const statusBodyTemplate = (rowData) => {
    return (
      <>
        <span className="p-column-title">Status</span>
        {rowData?.is_open ? (
          <span
            className="badge"
            style={{
              backgroundColor: '#10b981',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '500',
            }}
          >
            Active
          </span>
        ) : (
          <span className="text-muted">-</span>
        )}
      </>
    );
  };

  return (
    <div>
      <BatchCreateInvoicesDialog
        visible={createBatchVisible}
        onHide={() => setCreateBatchVisible(false)}
        onCreate={(payload) => {
          // Refetch the batch billing list after successful creation
          refetchBatchBilling();
          setSelectedRows([]);
        }}
        selectionCount={selectedRows?.length || 0}
        selectedCaseIds={selectedRows?.map((row) => row.case_uuid)}
        selectedRows={selectedRows}
      />

      <div className="">
        <div className="filter-wrap mb-3 p-3 w-100">
          <h5 className="mb-3 bb-subheading">Batch Billing Filters</h5>
          <div className="mb-3">
            <div className="p-medium mb-2 bb-subheading">Include in Selection:</div>
            <div className="d-flex flex-wrap align-items-center">
              <div className="d-flex align-items-center me-4 mb-2">
                <Checkbox inputId="bb_billable_time" checked={includeBillableTime} onChange={(e) => setIncludeBillableTime(e.checked)} />
                <label htmlFor="bb_billable_time" className="ms-2">
                  Uninvoiced Billable Time
                </label>
              </div>
              <div className="d-flex align-items-center me-4 mb-2">
                <Checkbox
                  inputId="bb_nonbillable_time"
                  checked={includeNonBillableTime}
                  onChange={(e) => setIncludeNonBillableTime(e.checked)}
                />
                <label htmlFor="bb_nonbillable_time" className="ms-2">
                  Uninvoiced Non-billable Time
                </label>
              </div>
              <div className="d-flex align-items-center me-4 mb-2">
                <Checkbox inputId="bb_expenses" checked={includeExpenses} onChange={(e) => setIncludeExpenses(e.checked)} />
                <label htmlFor="bb_expenses" className="ms-2">
                  Uninvoiced Expenses
                </label>
              </div>
              <div className="d-flex align-items-center me-4 mb-2">
                <Checkbox inputId="bb_unpaid_invoice" checked={includeUnpaidInvoice} onChange={(e) => setIncludeUnpaidInvoice(e.checked)} />
                <label htmlFor="bb_unpaid_invoice" className="ms-2">
                  Unpaid Invoice Balance
                </label>
              </div>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-12 col-lg-4">
              <div className="d-flex flex-column">
                <label className="p-medium mb-2">Practice Area</label>
                <Dropdown
                  value={selectedPractice}
                  options={[{ practice_area_name: 'All Practice Areas', practice_area_id: null }, ...(practiceAreaList || [])]}
                  optionLabel="practice_area_name"
                  optionValue="practice_area_id"
                  onChange={(e) => setSelectedPractice(e.value)}
                  placeholder="All Practice Areas"
                  className="input-shadow w-100"
                  showClear
                />
              </div>
            </div>
            <div className="col-12 col-lg-4">
              <div className="d-flex flex-column">
                <label className="p-medium mb-2">Lead Attorney</label>
                <Dropdown
                  value={selectedAttorney}
                  options={userList || []}
                  onChange={(e) => setSelectedAttorney(e.value)}
                  optionLabel={(v) => generateFullName(v)}
                  optionValue="firm_user_id"
                  placeholder="All Attorneys"
                  className="input-shadow w-100"
                  showClear
                  panelFooterTemplate={isFirmUserLoading ? <div className="p-2">Loading...</div> : null}
                />
              </div>
            </div>
            <div className="col-12 col-lg-4">
              <div className="d-flex flex-column">
                <label className="p-medium mb-2">Lead Paralegal</label>
                <Dropdown
                  value={selectedParalegal}
                  options={userList || []}
                  onChange={(e) => setSelectedParalegal(e.value)}
                  optionLabel={(v) => generateFullName(v)}
                  optionValue="firm_user_id"
                  placeholder="All Paralegals"
                  className="input-shadow w-100"
                  showClear
                  panelFooterTemplate={isFirmUserLoading ? <div className="p-2">Loading...</div> : null}
                />
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center mt-3">
            {(() => {
              const stdButtonStyle = {};
              return (
                <>
                  <Button
                    label="Apply Filters"
                    className="p-button p-button-primary me-2"
                    onClick={() => {
                      setAppliedFilters({
                        practice_area_list: selectedPractice ? [selectedPractice] : [],
                        lead_attorney_list: selectedAttorney ? [selectedAttorney] : [],
                        lead_paralegal_list: selectedParalegal ? [selectedParalegal] : [],
                        uninvoiced_billable_time: includeBillableTime,
                        uninvoiced_nonbillable_time: includeNonBillableTime,
                        uninvoiced_expenses: includeExpenses,
                        unpaid_invoice_balance: includeUnpaidInvoice,
                      });
                      setCurrentPage(1); // Reset to first page when filters are applied
                      // Save page reset to user preferences
                      const path = getFilePathForPagination();
                      handlePageNumberInPagination(userContext, path.module, path.subModule, 'batch_billing_page', 1);
                    }}
                    style={stdButtonStyle}
                  />
                  <Button
                    label="Clear Filters"
                    className="p-button p-button-primary me-2"
                    onClick={() => {
                      setIncludeBillableTime(true);
                      setIncludeNonBillableTime(false);
                      setIncludeExpenses(true);
                      setIncludeUnpaidInvoice(true);
                      setSelectedPractice(null);
                      setSelectedAttorney(null);
                      setSelectedParalegal(null);
                      setAppliedFilters({
                        practice_area_list: [],
                        lead_attorney_list: [],
                        lead_paralegal_list: [],
                        uninvoiced_billable_time: true,
                        uninvoiced_nonbillable_time: true,
                        uninvoiced_expenses: true,
                        unpaid_invoice_balance: true,
                      });
                      setSearchKey('');
                      setAppliedSearchKey('');
                      setCurrentPage(1); // Reset to first page when filters are cleared
                      // Save page reset to user preferences
                      const path = getFilePathForPagination();
                      handlePageNumberInPagination(userContext, path.module, path.subModule, 'batch_billing_page', 1);
                    }}
                    style={stdButtonStyle}
                  />
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Eligible Cases Header with Search and Select All/Deselect All */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center">
          <h5 className="mb-0 me-3">Eligible Cases</h5>
          {selectedRows?.length > 0 && (
            <span
              className="badge"
              style={{
                backgroundColor: '#0065bd',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500',
              }}
            >
              {selectedRows.length} selected
            </span>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={searchKey}
              onChange={handleSearchChange}
              placeholder="Search cases..."
              className="input-shadow"
              style={{ width: '250px' }}
            />
          </span>
          <Button
            label="Select All"
            className="p-button p-button-secondary"
            onClick={handleSelectAll}
            disabled={!batchBillingList || batchBillingList.length === 0}
          />
          <Button
            label="Deselect All"
            className="p-button p-button-secondary"
            onClick={handleDeselectAll}
            disabled={selectedRows?.length === 0}
          />
        </div>
      </div>

      <div className="col-12 p-0 datatable-responsive">
        <div className="bg-white shadow-middle mb-3">
          <ResizableDataTable
            value={batchBillingList || []}
            className="p-datatable-responsive"
            paginator={false}
            selection={selectedRows}
            onSelectionChange={(e) => setSelectedRows(e.value)}
            dataKey="case_id"
            showGridlines={false}
            loading={isBatchBillingLoading}
            columns={[
              { header: '', selectionMode: 'multiple', width: '40px', fixed: true, resizable: false },
              { field: 'case_name', header: 'Case', bodyClassName: 'ellipsis-text' },
              { field: 'contact_name', header: 'Client', bodyClassName: 'ellipsis-text' },
              { field: 'practice_area', header: 'Practice Area', bodyClassName: 'ellipsis-text' },
              { field: 'uninvoiced_billable_amount', header: 'Unbilled Time', bodyClassName: 'ellipsis-text' },
              { field: 'unbilled_expenses_amount', header: 'Unbilled Expense', bodyClassName: 'ellipsis-text' },
              { field: 'total_invoice_due_amount', header: 'Outstanding Balance', bodyClassName: 'ellipsis-text' },
              { field: 'total_unbilled_amount', header: 'Total Unbilled', bodyClassName: 'ellipsis-text' },
              // { field: 'is_open', header: 'Status', body: statusBodyTemplate, bodyClassName: 'ellipsis-text' },
            ]}
            emptyMessage={
              isBatchBillingLoading ? (
                <div className="p-3 text-center text-muted">Loading...</div>
              ) : isBatchBillingError ? (
                <div className="p-3 text-center">Error loading data. Please try again.</div>
              ) : (
                <div className="p-3 text-center text-muted">No records found</div>
              )
            }
          />
        </div>
        {/* External pagination using EllipsisPagination */}
        <EllipsisPagination
          currentPage={currentPage}
          totalRecords={totalRecords}
          pageSize={pageLimit}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          showPageSizeDropdown={true}
        />
      </div>

      {/* Proceed to Batch Billing Button */}
      <div className="d-flex justify-content-center m-3">
        <Button
          label={`Proceed to Batch Billing (${selectedRows?.length || 0} cases selected)`}
          className="p-button p-button-primary"
          icon="pi pi-caret-right"
          disabled={isProcessDisabled}
          onClick={() => setCreateBatchVisible(true)}
          style={{ minWidth: '300px' }}
        />
      </div>
    </div>
  );
};

export default BatchBillingV2;
